import { getActiveTypingHandles } from 'reduxes/slices/actives/selectors'
import { getSelectedTopicId } from 'reduxes/slices/selected/selectors'
import { createSelector } from 'reselect'

import { getApiParticipants } from '../api/selectors'
import { getCurrentHandle } from '../current/selectors'

export const makeGetParticipantsForTopic = () =>
  createSelector(
    [getApiParticipants, (_, topic) => topic],
    (participants, topic) => {
      const participantIds = topic?.participants || []
      if (!participantIds.length) {
        return []
      }
      return participantIds
        .map((id) => participants[id])
        .filter((participant) => !!participant)
        .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
    },
  )

export const makeGetHandlesOnTopic = () =>
  createSelector(
    [getApiParticipants, (_, topic) => topic],
    (participants, topic) => {
      const participantIds = topic?.participants || []
      if (!participantIds.length) {
        return []
      }

      return participantIds.reduce((acc, id) => {
        const handle = participants[id]?.handle
        if (handle) {
          acc.push(handle)
        }
        return acc
      }, [])
    },
  )

export const getTypingHandlesForTopic = createSelector(
  [getActiveTypingHandles, getSelectedTopicId, getCurrentHandle],
  (activeTypingHandles, topicId, currentHandle) => {
    if (!activeTypingHandles) return

    return Object.keys(activeTypingHandles).reduce((result, handle) => {
      if (handle === currentHandle) {
        return result
      }
      const topicIdForHandle = parseInt(activeTypingHandles[handle][0], 10)
      if (topicIdForHandle === topicId) {
        result.push(handle)
      }
      return result
    }, [])
  },
)

export const makeGetTopicParticipant = () =>
  createSelector(
    [
      getApiParticipants,
      (_, props) => props.topicId,
      (_, props) => props.handle,
    ],
    (participants, topicId, handle) => {
      if (!topicId || !handle) return

      return Object.values(participants).find(
        (p) =>
          p.handle === handle &&
          p.parent_type === 'Topic' &&
          p.parent_id === topicId,
      )
    },
  )

export const getOpenParticipantsWithCurrentHandle = createSelector(
  [getCurrentHandle, getApiParticipants],
  (currentHandle, participants) => {
    const participantsByHandle = Object.values(participants).reduce(
      (acc, participant) => {
        if (
          participant.handle === currentHandle &&
          participant.parent_type === 'Topic' &&
          (participant.parent_status === 'open' || participant.participant === 'silenced')
        ) {
          // just in case there are old participants in the redux (you have been removed and re-added)
          // should receive a broadcast that deletes old participants from redux, this is just a precaution
          const id = participant.id
          if (!acc[id] || id > acc[currentHandle].id) {
            acc[id] = participant
          }
        }
        return acc
      },
      {},
    )

    return Object.values(participantsByHandle)
  },
)
